import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectClient } from '@client/model';
import { useToast } from '@client/components/hooks/use-toast';
import { analytics } from '@shared/posthog';
import { useAnonymizedIntakeSections } from './api/use-anonymized-intake-sections';
import { useSendPuiEmail } from './api/use-send-pui-email';
import { useQueryClient } from '@tanstack/react-query';

type DialogStep = 'select-policies' | 'review-draft' | 'show-confirmation';

interface UseSendPuiDialogProps {
  open: boolean;
  onClose: () => void;
  availablePolicies: PolicyInfo[];
}

export function useSendPuiDialog({ open, onClose, availablePolicies }: UseSendPuiDialogProps) {
  const client = useSelector(selectClient);
  const clientId = client.clientId;
  const partnerClientId = client.partnerClientId;
  const { toast } = useToast();
  const [step, setStep] = useState<DialogStep>('select-policies');
  const [selectedPolicies, setSelectedPolicies] = useState<Set<string>>(new Set());
  const [showLoadingSequence, setShowLoadingSequence] = useState(false);
  const [hasTriggeredFetch, setHasTriggeredFetch] = useState(false);
  const [emailResults, setEmailResults] = useState<PostAnonymousPuiEmailResponse | undefined>(
    undefined,
  );
  const hasJointPolicy = availablePolicies.some(
    policy => selectedPolicies.has(policy.uniqueKey) && policy.isJoint,
  );
  const {
    data: anonymizedIntakeSections,
    refetch,
    isRefetchError,
  } = useAnonymizedIntakeSections(client?.clientId, hasJointPolicy);
  const queryClient = useQueryClient();

  const onSuccess = (emailResults: PostAnonymousPuiEmailResponse) => {
    setEmailResults(emailResults);
    queryClient.invalidateQueries({ queryKey: ['anonymousPuiHistory', clientId] });
  };

  const { mutate: sendPuiEmails, isPending, isSuccess } = useSendPuiEmail({ onSuccess });

  useEffect(() => {
    if (open) {
      setStep('select-policies');
      setSelectedPolicies(new Set());
      setShowLoadingSequence(false);
      setHasTriggeredFetch(false);
      setEmailResults(undefined);
    }
  }, [open]);

  useEffect(() => {
    if (
      anonymizedIntakeSections?.intakeSections &&
      !anonymizedIntakeSections.isLoading &&
      hasTriggeredFetch
    ) {
      setHasTriggeredFetch(false);
      setShowLoadingSequence(false);
      setStep('review-draft');
    } else if (anonymizedIntakeSections?.isLoading || hasTriggeredFetch) {
      setShowLoadingSequence(true);
    }
  }, [
    anonymizedIntakeSections?.isLoading,
    anonymizedIntakeSections?.intakeSections,
    hasTriggeredFetch,
  ]);

  useEffect(() => {
    if (anonymizedIntakeSections?.error) {
      setShowLoadingSequence(false);
      setHasTriggeredFetch(false);
      toast({
        title: 'Failed to anonymize intake data',
        description:
          anonymizedIntakeSections.error.message ||
          'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    }
  }, [anonymizedIntakeSections?.error, toast]);

  useEffect(() => {
    if (isSuccess && !isPending) {
      setStep('show-confirmation');
    }
  }, [isSuccess, isPending, onClose]);

  const handleTogglePolicy = useCallback((uniqueKey: string) => {
    setSelectedPolicies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(uniqueKey)) {
        newSet.delete(uniqueKey);
      } else {
        newSet.add(uniqueKey);
      }
      return newSet;
    });
  }, []);

  const handleBackToSelection = useCallback(() => {
    setStep('select-policies');
  }, []);

  const handleLoadingComplete = useCallback(() => {
    setShowLoadingSequence(false);
  }, []);

  const handleContinue = useCallback(async () => {
    if (!client?.clientId) return;
    const hasFetchedIntakeSections = !!anonymizedIntakeSections?.intakeSections;
    const hasFetchedPartnerIntakeSections = !!anonymizedIntakeSections?.partnerIntakeSections;

    // If we already have draft data and it's not loading, just navigate to review
    if (
      !anonymizedIntakeSections?.isLoading &&
      ((hasFetchedIntakeSections && !hasJointPolicy) ||
        (hasFetchedPartnerIntakeSections && hasJointPolicy))
    ) {
      setStep('review-draft');
      return;
    }

    // Trigger the fetch, which will start polling
    setShowLoadingSequence(true);
    setHasTriggeredFetch(true);

    const result = await refetch();

    if (result.isError || result.error) {
      setShowLoadingSequence(false);
      setHasTriggeredFetch(false);
      toast({
        title: 'Failed to fetch draft',
        description: result.error?.message || 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    }
    // Don't navigate here - the useEffect above will handle navigation once data loads
  }, [
    selectedPolicies,
    client?.clientId,
    hasJointPolicy,
    anonymizedIntakeSections?.intakeSections,
    anonymizedIntakeSections?.partnerIntakeSections,
    anonymizedIntakeSections?.isLoading,
    refetch,
    toast,
  ]);

  const handleSend = useCallback(
    (partialParams: SendEmailParams) => {
      const selectedPolicyData = availablePolicies
        .filter(policy => selectedPolicies.has(policy.uniqueKey))
        .map(policy => ({
          productName: policy.productName,
          fundingPolicyType: policy.fundingPolicyType,
          isJoint: policy.isJoint,
        }));

      const products = selectedPolicyData.map(policy => policy.productName);

      analytics.pui.submitted({
        clientId,
        products,
      });

      const params: PostAnonymousPuiEmailRequestParams = {
        anonymousIdentifier: partialParams.anonymousIdentifier,
        policies: selectedPolicyData,
        clientId,
        clientIntakeSections: partialParams.intakeSections,
        partnerClientId: hasJointPolicy ? partnerClientId! : undefined,
        partnerIntakeSections: partialParams.partnerIntakeSections,
        customMessages: partialParams.customMessages,
      };
      sendPuiEmails(params);
    },
    [availablePolicies, selectedPolicies, hasJointPolicy, partnerClientId, clientId, sendPuiEmails],
  );

  return {
    step,
    selectedPolicies,
    showLoadingSequence,
    draftData: anonymizedIntakeSections,
    emailResults,
    isPending,
    isSuccess,
    handleTogglePolicy,
    handleContinue,
    handleBackToSelection,
    handleLoadingComplete,
    handleSend,
  };
}
